import type { Cover } from "@paginae/blocks";
import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { api } from "../api.ts";
import { rememberAddress } from "../addresses.ts";

/**
 * "shared": pages shared with the viewer from parts of the tree they cannot see; "teamspace": a
 * teamspace's pages (ADR-0030), with `teamspaceId`.
 */
export type Section = "workspace" | "private" | "shared" | "teamspace";
export type Level = "full_access" | "can_edit" | "can_edit_content" | "can_comment" | "can_view";

export interface Workspace {
  id: string;
  name: string;
  icon: string | null;
  /** Its top-level pages' language (ADR-0036); null is the installation's default. */
  language: string | null;
  /** Whether members may subscribe to its database views in calendar apps (ADR-0062). */
  calendarFeeds: boolean;
  /** Whether its owners approved tools people may connect (ADR-0065). */
  hasTools?: boolean;
  /** Its main address while it answers (ADR-0098), where the switcher opens it. */
  address?: string | null;
  /** Null for someone outside the workspace who was given some of its pages (a reader). */
  role: "owner" | "membership_admin" | "member" | "guest" | null;
}

export interface TreePage {
  id: string;
  parentId: string | null;
  title: string;
  icon: string | null;
  section: Section;
  /** The teamspace the page is in, if any. */
  teamspaceId?: string | null;
  /** A page, a database, or a database's row (ADR-0031). */
  kind?: "page" | "database" | "row";
  createdAt?: number;
  lastEditedAt?: number;
  /** The viewer's access, which decides the actions offered. */
  level: Level;
  /** Its other names (an Aliases property, ADR-0087), for links and search. */
  aliases?: string[];
  /** Its readable address on the workspace's addresses (ADR-0098 stage 2), once it has one. */
  path?: string;
}

/** A teamspace (ADR-0030), as the viewer sees it. */
export interface Teamspace {
  id: string;
  name: string;
  icon: string | null;
  description: string;
  privacy: "open" | "closed" | "private";
  memberLevel: Level;
  archived: boolean;
  /** The viewer's role in it; null if they are not a member. */
  role: "owner" | "member" | null;
  members: number;
}

export const teamspacesQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspace", workspaceId, "teamspaces"],
    queryFn: () =>
      api<{ teamspaces: Teamspace[] }>(`/workspaces/${workspaceId}/teamspaces`).then(
        (r) => r.teamspaces,
      ),
  });

export interface PageDetail {
  page: Omit<TreePage, "level"> & {
    workspaceId: string;
    createdAt: number;
    lastEditedAt: number;
    /** Its cover (ADR-0077). */
    cover: Cover | null;
    /** Offered in the workspace's templates gallery (ADR-0089). */
    isTemplate?: boolean;
  };
  /** Its readable address on the workspace's addresses (ADR-0098 stage 2). */
  address?: string | null;
  level: Level;
  /** In the trash itself or through an ancestor. */
  inTrash: boolean;
  /** Deleted itself (so it can be restored), not only through an ancestor. */
  deleted: boolean;
  /** Archived itself (ADR-0025). */
  archived: boolean;
  /** Archived itself or through an ancestor: out of the sidebar and search. */
  inArchive: boolean;
  /** Locked: nobody edits it until unlocked (ADR-0029). */
  locked: boolean;
  /** Full width, its own setting or the nearest above; full when none is set (ADR-0049). */
  fullWidth: boolean;
  breadcrumbs: { id: string; title: string; icon: string | null }[];
  /** Its own language, and the one it follows otherwise (ADR-0036). */
  language: { own: string | null; inherited: string; language: string };
  /** Verified, by whom and until when (null: indefinitely) (ADR-0084); null if not. */
  verification: Verification | null;
  /** A database made from a page by "Turn into wiki" (ADR-0084). */
  wiki: boolean;
}

export interface Verification {
  by: { id: string; name: string };
  at: number;
  until: number | null;
  expired: boolean;
}

export interface TrashedPage {
  id: string;
  title: string;
  icon: string | null;
  section: Section;
  deletedAt: number;
  deletedBy: string | null;
}

/** An archived page (ADR-0025). */
export interface ArchivedPage {
  id: string;
  title: string;
  icon: string | null;
  archivedAt: number;
  archivedBy: string | null;
  level: Level;
}

/** A page shared with the viewer, wherever it is ("Shared with me"). */
export interface SharedPage {
  id: string;
  title: string;
  icon: string | null;
  workspaceId: string;
  workspace: string;
  level: Level;
  /** Only some blocks are shared; the page's title is not shown (ADR-0027). */
  blocksOnly?: boolean;
}

export const sharedQuery = queryOptions({
  queryKey: ["shared"],
  queryFn: () => api<{ pages: SharedPage[] }>("/shared").then((r) => r.pages),
});

/** Whether a level allows at least what `required` does. */
export function atLeast(level: Level | undefined, required: Level): boolean {
  const order: Level[] = ["can_view", "can_comment", "can_edit_content", "can_edit", "full_access"];
  return level !== undefined && order.indexOf(level) >= order.indexOf(required);
}

export const workspacesQuery = queryOptions({
  queryKey: ["workspaces"],
  queryFn: () => api<{ workspaces: Workspace[] }>("/workspaces").then((r) => r.workspaces),
});

export interface Member {
  userId: string;
  name: string;
  email: string;
  role: "owner" | "member" | "membership_admin" | "guest";
  joinedAt: number;
}

/** The workspace's members (not visible to guests). */
export const membersQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspace", workspaceId, "members"],
    queryFn: () => api<{ members: Member[] }>(`/workspaces/${workspaceId}/members`),
  });

export const treeQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspace", workspaceId, "pages"],
    queryFn: () =>
      api<{ pages: TreePage[] }>(`/workspaces/${workspaceId}/pages`).then((r) => {
        for (const p of r.pages) rememberAddress(p.id, p.path);
        return r.pages;
      }),
  });

export const trashQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspace", workspaceId, "trash"],
    queryFn: () =>
      api<{ pages: TrashedPage[] }>(`/workspaces/${workspaceId}/trash`).then((r) => r.pages),
  });

export const archiveQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspace", workspaceId, "archive"],
    queryFn: () =>
      api<{ pages: ArchivedPage[] }>(`/workspaces/${workspaceId}/archive`).then((r) => r.pages),
  });

export const pageQuery = (pageId: string) =>
  queryOptions({
    queryKey: ["page", pageId],
    queryFn: () =>
      api<PageDetail>(`/pages/${pageId}`).then((d) => {
        rememberAddress(d.page.id, d.address);
        return d;
      }),
  });

/** After any change to the tree: the sidebar, the trash and open pages may all be affected. */
export async function refreshWorkspace(queryClient: QueryClient, workspaceId: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["workspace", workspaceId] }),
    queryClient.invalidateQueries({ queryKey: ["page"] }),
  ]);
}

export async function createPage(
  workspaceId: string,
  dest: {
    parentId: string | null;
    section?: Section;
    teamspaceId?: string;
    /** A database rather than a page (ADR-0031). */
    kind?: "database";
    /** Its title from the start ("+name" in a page, ADR-0087). */
    title?: string;
  },
): Promise<string> {
  const { page } = await api<{ page: { id: string } }>(`/workspaces/${workspaceId}/pages`, {
    method: "POST",
    json: dest,
  });
  return page.id;
}

/** Children of each page (key null: the roots), in order, for one section. */
export function childrenMap(
  pages: TreePage[],
  section: Section,
  teamspaceId: string | null = null,
): Map<string | null, TreePage[]> {
  const map = new Map<string | null, TreePage[]>();
  for (const p of pages) {
    if (p.section !== section) continue;
    if (section === "teamspace" && p.teamspaceId !== teamspaceId) continue;
    map.set(p.parentId, [...(map.get(p.parentId) ?? []), p]);
  }
  return map;
}

/** The pages beside `page` in the sidebar: same parent and section, in their order. */
export function siblingsOf(pages: TreePage[], page: TreePage): TreePage[] {
  return pages.filter(
    (p) =>
      p.parentId === page.parentId &&
      p.section === page.section &&
      (p.teamspaceId ?? null) === (page.teamspaceId ?? null),
  );
}

/** IDs of a page and all its descendants, to exclude them as move destinations. */
export function descendantsOf(pages: TreePage[], id: string): Set<string> {
  const out = new Set([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const p of pages) {
      if (p.parentId !== null && out.has(p.parentId) && !out.has(p.id)) {
        out.add(p.id);
        grew = true;
      }
    }
  }
  return out;
}

/** What someone may do in a workspace beyond their page access (ADR-0070). */
export interface Capabilities {
  publish: boolean;
  export: boolean;
  shareOutside: boolean;
}
export interface CapabilityPolicy {
  who: "everyone" | "only" | "nobody";
  admins: boolean;
  owners: boolean;
  groups: string[];
}
export interface WorkspaceRule {
  who: "everyone" | "owners" | "groups";
  groups: string[];
}
export interface CapabilitiesResponse {
  mine: Capabilities;
  /** For the workspace's owners and administrators. */
  rules?: Partial<Record<keyof Capabilities, WorkspaceRule>>;
  installation?: Record<keyof Capabilities, CapabilityPolicy>;
  groups?: { id: string; name: string }[];
}
export const capabilitiesQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspace-capabilities", workspaceId],
    queryFn: () => api<CapabilitiesResponse>(`/workspaces/${workspaceId}/capabilities`),
    staleTime: 60_000,
  });
